import React, { useState, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';

interface CategoriaSearchProps {
  value: string;
  onChange: (query: string) => void;
  onSearch: (query: string) => Promise<void>;
  loading?: boolean;
  placeholder?: string;
}

export function CategoriaSearch({
  value,
  onChange,
  onSearch,
  loading = false,
  placeholder = 'Buscar categorías o subcategorías...',
}: CategoriaSearchProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback(
    async (query: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (query.length === 0) {
        await onSearch('');
        return;
      }

      setInternalLoading(true);
      debounceTimerRef.current = setTimeout(async () => {
        try {
          await onSearch(query);
        } finally {
          setInternalLoading(false);
        }
      }, 300);
    },
    [onSearch]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    handleSearch(newValue);
  };

  const handleClear = () => {
    onChange('');
    handleSearch('');
  };

  const isLoading = loading || internalLoading;

  return (
    <TextField
      fullWidth
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      size="small"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={20} />
              ) : value ? (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
